import React, { useEffect, useRef } from 'react';
import { RouterProvider } from 'react-router-dom';
import { App as AntApp, ConfigProvider } from 'antd';
import ruRU from 'antd/locale/ru_RU';
import enUS from 'antd/locale/en_US';
// Названия месяцев и дней в календаре antd печатает dayjs, а не сам antd:
// русская локаль подключается явно (английская у dayjs встроена).
import * as dayjsModule from 'dayjs';
import 'dayjs/locale/ru';
import { useAppDispatch, useAppSelector } from '@/hooks/hooks';
import { fetchMeThunk } from '@/store/authSlice';
import { fetchProjectsOnLoadThunk, fetchProjectsThunk } from '@/store/projectsSlice';
import { setActiveOrg } from '@/store/organizationsSlice';
import { router } from '@/router';
import { buildAppTheme } from '@/theme/antdTheme';
import { KitMessageHost } from '@/components/layout/kitMessage';
import { renderKitEmpty } from '@/components/kit/ds/renderKitEmpty';
import { useResolvedTheme } from '@/theme/useResolvedTheme';
import { publicSafeHtmlTheme } from '@/theme/publicSafeTheme';
import { isPublicPlayerContext } from '@/utils/publicHost';
import { TOKEN_KEY } from '@/api/client';
import { shouldReloadOnTokenChange } from '@/utils/auth/multiTabTokenSync';
import { rememberUiLocale, setCurrentUiLocale, useUiLocaleSource } from '@/i18n';
// WEB-1928 (в): единственная реакция кабинета на автопоправку активной
// организации (перезапрос проектов + запись выбора на сервер). Живёт здесь,
// потому что AppInitializer смонтирован всё время жизни кабинета, а поправка
// приходит со списком организаций, который тянет гейт ниже по дереву.
import { useActiveOrgSync } from '@/hooks/useActiveOrgSync';
import type { User } from '@/types';

// dayjs — модуль в формате CommonJS; такая форма импорта работает и в Vite, и в
// Jest (тот же приём, что в components/statistics/PeriodPicker.tsx).
const dayjs =
  (dayjsModule as unknown as { default?: { locale: (l?: string) => string } })
    .default ?? (dayjsModule as unknown as { locale: (l?: string) => string });

function AppInitializer() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  // WEB-329: read the persisted activeOrgId at render time so the
  // isAuthenticated effect (login transition) can scope the initial project
  // list without needing another /auth/me round-trip.
  const authUser = useAppSelector((state) => state.auth.user);

  useActiveOrgSync();

  useEffect(() => {
    // On page load: GET /auth/me → push server-persisted activeOrgId into the
    // organizations store before fetchOrganizationsThunk auto-selects first org.
    dispatch(fetchMeThunk()).then((result) => {
      if (result.meta.requestStatus === 'fulfilled') {
        const user = result.payload as User;
        if (user.activeOrgId) {
          dispatch(setActiveOrg(user.activeOrgId));
        }
        dispatch(fetchProjectsOnLoadThunk(user.activeOrgId ?? undefined));
      }
    });
  }, [dispatch]);

  /**
   * WEB-1368 п.1.11 — список проектов запрашивался ДВАЖДЫ при каждой загрузке
   * кабинета.
   *
   * Эффект выше (GET /auth/me) уже зовёт fetchProjectsThunk, а этот эффект на
   * первой отрисовке видел isAuthenticated=true (токен в хранилище) и звал
   * его второй раз с тем же идентификатором организации. Подмены чужих данных
   * не было — просто лишний запрос на каждом заходе.
   *
   * Этот эффект нужен ТОЛЬКО для перехода false→true (ручной вход), поэтому
   * теперь он и реагирует ровно на переход, а не на «истинно с рождения».
   * Источник вызова на загрузке остаётся один — ответ /auth/me.
   */
  const wasAuthenticated = useRef(isAuthenticated);
  useEffect(() => {
    const justLoggedIn = isAuthenticated && !wasAuthenticated.current;
    wasAuthenticated.current = isAuthenticated;
    if (!justLoggedIn) return;
    // authUser is current at render time since loginThunk sets it synchronously
    // before React re-renders. Intentionally excluded from deps to avoid
    // re-triggering on profile updates.
    if (authUser?.activeOrgId) {
      dispatch(setActiveOrg(authUser.activeOrgId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    dispatch(fetchProjectsThunk(authUser?.activeOrgId ?? undefined));
  }, [dispatch, isAuthenticated]); // authUser intentionally omitted from deps

  useEffect(() => {
    // WEB-636: the access token lives in a single shared localStorage key, so a
    // login in another tab overwrites it for this tab too — the stale tab would
    // keep sending a token that now belongs to a DIFFERENT user (403s / silent
    // wrong-account). React to the cross-tab `storage` event and reload so this
    // tab re-bootstraps with the current token (or lands on /login after a
    // logout). A same-user token refresh (sub unchanged) does NOT reload.
    const onStorage = (e: StorageEvent) => {
      if (e.key !== TOKEN_KEY) return;
      if (shouldReloadOnTokenChange(e.oldValue, e.newValue)) {
        window.location.reload();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return null;
}

export default function App() {
  // WEB-386 PM-4: применяем выбранную пользователем тему (system/light/dark).
  // system — вживую следует теме устройства (см. useResolvedTheme).
  const userTheme = useAppSelector((state) => state.auth.user?.theme);
  const resolvedTheme = useResolvedTheme(userTheme);

  // WEB-490 (QA bounce off 793cfba): the public player is served on user-owned
  // custom domains, but also via the editor's draft preview (?preview=1) and
  // legacy-path-mode dev URLs on the apex host / localhost. An unauthenticated
  // visitor has no persisted theme there, so `resolvedTheme` follows the
  // visitor's OS. A dark-OS visitor would get data-theme='dark' → white
  // --color-text-primary → default-theme funnels render white-on-white. Force
  // light in every public-player context, host-independently; a funnel's own
  // palette applies inline and overrides it. The cabinet keeps its own
  // resolved theme (isPublicPlayerContext is false there).
  const htmlTheme = publicSafeHtmlTheme(resolvedTheme, isPublicPlayerContext());

  // data-theme на <html> переключает палитру CSS-переменных (global.css) для
  // кастомных стилей; color-scheme — нативные скроллбары/контролы. Фон body берётся
  // из var(--color-bg-secondary), который flips по data-theme.
  useEffect(() => {
    document.documentElement.dataset.theme = htmlTheme;
    document.documentElement.style.colorScheme = htmlTheme;

    // WEB-806: тот же ключ читает boot-скрипт в index.html, чтобы следующая
    // перезагрузка стартовала сразу в нужной теме, а не мигала светлым до
    // загрузки бандла. Публичный плеер не сохраняем: там тема принудительно
    // светлая, и записывать её как «выбор пользователя» нельзя — кабинет в
    // том же браузере после этого стартовал бы белым.
    if (!isPublicPlayerContext()) {
      try {
        localStorage.setItem('ui-theme', htmlTheme);
      } catch {
        // Приватный режим/заблокированное хранилище — просто без ускорения.
      }
    }
  }, [htmlTheme]);

  // WEB-615: до этого локаль не передавалась вообще, и весь встроенный текст
  // antd в кабинете оставался английским — календарь («Aug 2026», «Su Mo
  // Tu…»), постраничная навигация («20 / page»), скрытые подписи «Select all»
  // / «Close» / «No data». Одна корневая локаль снимает это разом.
  //
  // WEB-1874: локаль больше не зашита русской. Тогда кабинет был русским по
  // решению PM, теперь язык — свойство человека (`users.language`, умолчание
  // `en`, решение владельца 04.09), и локаль antd обязана идти за ним: иначе
  // англоязычный человек получил бы английские надписи и русский календарь.
  //
  // Публичный плеер сюда по-прежнему НЕ попадает: там язык задаёт сама воронка
  // (решение PM 2026-08-03, зона WEB-785) — системные подписи кабинета в
  // чужеязычной воронке были бы такой же ошибкой, только зеркальной.
  const { locale: uiLocale, fromProfile: localeFromProfile } = useUiLocaleSource();
  const locale = isPublicPlayerContext()
    ? undefined
    : uiLocale === 'ru'
      ? ruRU
      : enUS;

  useEffect(() => {
    // Одна точка синхронизации языка на всё приложение:
    //  - модульная переменная — ею живёт код ВНЕ React (thunk'и, утилиты,
    //    тосты из сервисов), иначе тост говорил бы не на том языке, что экран;
    //  - локаль dayjs — им antd печатает названия месяцев и дней в календаре,
    //    иначе рядом с английскими надписями стоял бы русский «Август»;
    //  - localStorage — чтобы СЛЕДУЮЩАЯ загрузка (и экран входа, где профиля
    //    ещё нет) рисовалась сразу на языке человека, без мигания;
    //  - <html lang> — для браузера, скринридеров и переносов слов.
    // Плеер не трогаем: там правит язык воронки, и записать его как «выбор
    // человека» значило бы перекрасить кабинет в том же браузере.
    setCurrentUiLocale(uiLocale);
    if (!isPublicPlayerContext()) {
      dayjs.locale(uiLocale);
      // Запоминаем ТОЛЬКО язык из профиля (ревизия пачки 15, R1-14): догадка
      // (браузер/прежняя запись) в хранилище не попадает — запомненное
      // читается раньше браузера, и однажды записанная догадка перебивала бы
      // его навсегда, а на общем устройстве доставалась бы следующему гостю.
      if (localeFromProfile) rememberUiLocale(uiLocale);
      document.documentElement.lang = uiLocale;
    }
  }, [uiLocale, localeFromProfile]);

  return (
    // WEB-4003: пустое состояние таблиц и списков antd — общее кита с картинкой сервиса
    <ConfigProvider
      theme={buildAppTheme(htmlTheme)}
      locale={locale}
      renderEmpty={renderKitEmpty}
    >
      <AntApp>
        {/* WEB-3767 п. 9: всплывашки antd (message корня, статический, экземпляры) — тостом кита */}
        <KitMessageHost>
          <AppInitializer />
          <RouterProvider router={router} />
        </KitMessageHost>
      </AntApp>
    </ConfigProvider>
  );
}
